import {
  RaceSessionLifecycle,
  type RaceSessionMode,
} from './race-session-lifecycle.js';
import { RuntimeScope, type OwnedRuntimeResource } from './runtime-scope.js';
import type { RaceDiagnostics } from './race-diagnostics.js';

export interface RaceRuntimeTransport {
  close(): void;
}

export interface RaceRuntimePresentation {
  destroy(): void;
}

export class RaceRuntimeInvariantError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RaceRuntimeInvariantError';
  }
}

/** Owns the replaceable transport/presentation pair for exactly one session. */
export class RaceRuntimeController<
  TTransport extends RaceRuntimeTransport,
  TPresentation extends RaceRuntimePresentation,
> {
  readonly lifecycle = new RaceSessionLifecycle();
  private activeScope: RuntimeScope | null = null;
  private transportResource: OwnedRuntimeResource<TTransport> | null = null;
  private presentationResource: OwnedRuntimeResource<TPresentation> | null = null;

  constructor(private readonly diagnostics?: RaceDiagnostics) {}

  get scope(): RuntimeScope | null {
    return this.activeScope;
  }

  get transport(): TTransport | null {
    return this.transportResource?.value ?? null;
  }

  get presentation(): TPresentation | null {
    return this.presentationResource?.value ?? null;
  }

  begin(roomId: string, mode: RaceSessionMode, attempt = 1): RuntimeScope {
    this.disposeResources();
    this.lifecycle.begin(roomId, mode, attempt);
    this.activeScope = new RuntimeScope();
    this.diagnostics?.record('runtime', 'session-begin', { roomId, mode, attempt });
    this.assertInvariant();
    return this.activeScope;
  }

  setTransport(transport: TTransport | null): void {
    if (transport === this.transport) return;
    this.transportResource?.dispose();
    this.transportResource = null;
    if (!transport) {
      this.diagnostics?.record('runtime', 'transport-cleared');
      return;
    }
    const scope = this.requireScope();
    this.transportResource = scope.own(transport, (owned) => owned.close());
    this.diagnostics?.record('runtime', 'transport-mounted', {
      kind: transport.constructor?.name ?? 'transport',
    });
    this.assertInvariant();
  }

  setPresentation(presentation: TPresentation | null): void {
    if (presentation === this.presentation) return;
    this.presentationResource?.dispose();
    this.presentationResource = null;
    if (!presentation) {
      this.diagnostics?.record('runtime', 'presentation-cleared');
      return;
    }
    const scope = this.requireScope();
    this.presentationResource = scope.own(presentation, (owned) => owned.destroy());
    this.diagnostics?.record('runtime', 'presentation-mounted', {
      kind: presentation.constructor?.name ?? 'presentation',
    });
    this.assertInvariant();
  }

  dispose(): readonly unknown[] {
    const errors = this.disposeResources();
    this.lifecycle.close();
    this.diagnostics?.record('runtime', 'session-disposed', { errors: errors.length });
    this.assertInvariant();
    return errors;
  }

  /** Fails close to the mutation that split resource and lifecycle identity. */
  assertInvariant(): void {
    const lifecycleActive = this.lifecycle.state.phase !== 'idle';
    const scopeActive = this.activeScope !== null && !this.activeScope.disposed;
    if (lifecycleActive !== scopeActive) {
      this.diagnostics?.record('runtime', 'invariant-failed', {
        invariant: 'lifecycle-scope-pair', lifecycleActive, scopeActive,
      });
      throw new RaceRuntimeInvariantError(
        'Race lifecycle and runtime scope must become active and idle together',
      );
    }
    if (!scopeActive && (this.transportResource || this.presentationResource)) {
      this.diagnostics?.record('runtime', 'invariant-failed', {
        invariant: 'idle-resource-retention',
      });
      throw new RaceRuntimeInvariantError('An idle race runtime cannot retain owned resources');
    }
  }

  private disposeResources(): readonly unknown[] {
    this.presentationResource?.dispose();
    this.transportResource?.dispose();
    this.presentationResource = null;
    this.transportResource = null;
    const errors = this.activeScope?.dispose() ?? [];
    this.activeScope = null;
    return errors;
  }

  private requireScope(): RuntimeScope {
    if (!this.activeScope || this.activeScope.disposed) {
      throw new Error('Race runtime resources require an active session scope');
    }
    return this.activeScope;
  }
}
